import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../components/Icon';
import Pagination, { PAGE_SIZE } from '../components/Pagination';
import KpiCard from '../components/KpiCard';
import {
  RevenueBreakdownRow,
  RevenueKpis,
  RevenuePeriod,
  Transaction,
  TransactionType,
  getRevenueSummary,
  listTransactions,
} from '../api/revenue';
import { extractApiError } from '../api/legalServices';

type TxnFilter = 'all' | TransactionType;

/*
 * The platform earns from three streams: paid service orders — legal services
 * and maintenance requests — (stored as 'Commission' transactions), rental
 * application fees ('App Fee') and subscriptions. 'Listing Fee' is never
 * recorded, so it is dropped. Commission is shown as "Legal Services Request" and
 * App Fee as "Rental Application", matching the sidebar pages they come
 * from. Payout (money out) is kept.
 */
const FILTERS: TxnFilter[] = [
  'all',
  'Commission',
  'App Fee',
  'Subscription',
];

/** Display label for a raw transaction type. */
const typeLabel = (type: TransactionType): string => {
  switch (type) {
    case 'Commission':
      return 'Legal Services Request';
    case 'App Fee':
      return 'Rental Application';
    default:
      return type;
  }
};

/**
 * Who the money came from: the customer for legal services and rental
 * applications, the merchant (agent) for subscriptions. "All" mixes both.
 */
const partyLabel = (filter: TxnFilter): string => {
  if (filter === 'all') return 'Customer / Merchant';
  return filter === 'Subscription' ? 'Merchant' : 'Customer';
};

const txnTypeStyle = (type: TransactionType): React.CSSProperties => {
  switch (type) {
    case 'Commission':
      return { background: 'var(--brand-bg)', color: 'var(--brand)' };
    case 'App Fee':
      return { background: 'var(--green-bg)', color: 'var(--green)' };
    case 'Subscription':
      return { background: 'var(--blue-bg)', color: 'var(--blue)' };
    default:
      return { background: 'var(--g100)', color: 'var(--g600)' };
  }
};

/** The three revenue streams, in the chart's colours. */
const STREAMS = [
  { type: 'Commission', label: 'Legal Services Request', icon: 'scale', color: 'var(--brand)', bg: 'var(--brand-bg)' },
  { type: 'App Fee', label: 'Rental Application', icon: 'file-text', color: 'var(--green)', bg: 'var(--green-bg)' },
  { type: 'Subscription', label: 'Subscriptions', icon: 'credit-card', color: 'var(--blue)', bg: 'var(--blue-bg)' },
] as const;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** YYYY-MM-DD for a local date (the date filter's format). */
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const money = (n: number | string | null | undefined): string => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '$0';
  return '$' + Math.round(v).toLocaleString();
};

const moneyExact = (n: number | string | null | undefined): string => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return '$' + v.toFixed(2);
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const Revenue: React.FC = () => {
  const [kpis, setKpis] = useState<RevenueKpis | null>(null);
  const [breakdown, setBreakdown] = useState<RevenueBreakdownRow[]>([]);
  const [txns, setTxns] = useState<Transaction[]>([]);

  const [filter, setFilter] = useState<TxnFilter>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // What's typed in the search box, and the search last run with the button.
  const [search, setSearch] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [period, setPeriod] = useState<RevenuePeriod | null>(null);
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [years, setYears] = useState<number[]>(() => [new Date().getFullYear()]);
  // Month shown in the Monthly payments cards (0 = January).
  const [month, setMonth] = useState<number>(() => new Date().getMonth());
  const txnSectionRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [txnLoading, setTxnLoading] = useState<boolean>(true);
  const [txnTotal, setTxnTotal] = useState<number>(0);

  // Cards, chart and period totals: the dates and year only.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const range = { from: dateFrom || undefined, to: dateTo || undefined };
      const summary = await getRevenueSummary({ ...range, year });
      setKpis(summary.kpis);
      setBreakdown(summary.breakdown);
      if (Array.isArray(summary.availableYears) && summary.availableYears.length) {
        setYears(summary.availableYears);
      }
      setPeriod(summary.period ?? null);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load revenue data'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, year]);

  useEffect(() => {
    load();
  }, [load]);

  // Transactions table: one page at a time. Changing a filter (type tab,
  // search, dates) goes straight back to page 1.
  const txnKey = [filter, appliedSearch, dateFrom, dateTo].join('|');
  const [paging, setPaging] = useState<{ key: string; page: number }>({ key: txnKey, page: 1 });
  const page = paging.key === txnKey ? paging.page : 1;
  const setPage = useCallback((next: number) => setPaging({ key: txnKey, page: next }), [txnKey]);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try {
      const res = await listTransactions({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        type: filter === 'all' ? undefined : filter,
        q: appliedSearch || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setTxns(res.transactions);
      setTxnTotal(res.total);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load transactions'));
    } finally {
      setTxnLoading(false);
    }
  }, [dateFrom, dateTo, filter, appliedSearch, page]);

  useEffect(() => {
    loadTxns();
  }, [loadTxns]);

  const hasRange = Boolean(dateFrom || dateTo);
  const hasFilters = hasRange || Boolean(search || appliedSearch);
  const rangeLabel =
    dateFrom && dateTo
      ? `${formatDate(dateFrom + 'T00:00:00')} – ${formatDate(dateTo + 'T00:00:00')}`
      : dateFrom
      ? `Since ${formatDate(dateFrom + 'T00:00:00')}`
      : `Up to ${formatDate(dateTo + 'T00:00:00')}`;

  // Year stepper: `years` is newest first.
  const yearIdx = years.indexOf(year);
  const olderYear = yearIdx >= 0 && yearIdx < years.length - 1 ? years[yearIdx + 1] : null;
  const newerYear = yearIdx > 0 ? years[yearIdx - 1] : null;

  const now = new Date();
  const isFutureMonth = (monthIdx: number): boolean =>
    year > now.getFullYear() || (year === now.getFullYear() && monthIdx > now.getMonth());
  const yearHasRevenue = breakdown.some((b) => b.total > 0);

  // When the year changes, start the cards on this month (current year) or
  // on the latest month with revenue (other years). Only on a year change —
  // the data also reloads on every date-filter change, and that must not
  // undo the month the admin picked.
  const monthYearRef = useRef<number | null>(null);
  useEffect(() => {
    if (monthYearRef.current === year) return;
    if (year === new Date().getFullYear()) {
      monthYearRef.current = year;
      setMonth(new Date().getMonth());
      return;
    }
    if (!breakdown.length || breakdown[0].year !== year) return; // wait for that year's data
    monthYearRef.current = year;
    let last = -1;
    breakdown.forEach((b, i) => {
      if (b.total > 0) last = i;
    });
    setMonth(last >= 0 ? last : 11);
  }, [year, breakdown]);

  const monthRow = breakdown[month];
  const monthLabel = `${MONTH_NAMES[month]} ${year}`;

  // "View payments": show that stream's transactions for the chosen month.
  const viewPayments = (type: TxnFilter) => {
    setFilter(type);
    setDateFrom(isoDay(new Date(year, month, 1)));
    setDateTo(isoDay(new Date(year, month + 1, 0)));
    txnSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const max = useMemo(() => {
    if (breakdown.length === 0) return 1;
    const totals = breakdown.map((b) => b.total);
    const m = Math.max(...totals);
    return m > 0 ? m : 1; // avoid divide-by-zero in the chart math
  }, [breakdown]);

  return (
    <div>
      {/* Year selector — drives the year card and the chart */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="text-sm text-g500">
          Revenue overview for <strong className="text-ink">{year}</strong>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-g600">Year</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => olderYear !== null && setYear(olderYear)}
            disabled={olderYear === null}
            style={olderYear === null ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            aria-label="Previous year"
            title="Previous year"
          >
            <Icon name="arrow-left" size={14} />
          </button>
          <select
            className="form-input"
            style={{ width: 110, padding: '7px 12px', fontWeight: 700 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="Year"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => newerYear !== null && setYear(newerYear)}
            disabled={newerYear === null}
            style={newerYear === null ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            aria-label="Next year"
            title="Next year"
          >
            <Icon name="chevron-right" size={14} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="flex gap-4 flex-wrap mb-6">
        <KpiCard
          icon="calendar"
          label={`Revenue in ${year}`}
          value={loading ? '…' : money(kpis?.yearRevenue ?? 0)}
          change=""
          up
          delay={0}
        />
        <KpiCard
          icon="dollar-sign"
          label="Total Revenue (all time)"
          value={loading ? '…' : money(kpis?.totalRevenue ?? 0)}
          change=""
          up
          delay={60}
        />
        <KpiCard
          icon="bar-chart-3"
          label="Revenue This Month"
          value={loading ? '…' : money(kpis?.monthRevenue ?? 0)}
          change=""
          up
          delay={120}
        />
        <KpiCard
          icon="trending-up"
          label="Revenue This Week"
          value={loading ? '…' : money(kpis?.weekRevenue ?? 0)}
          change=""
          up
          delay={180}
        />
      </div>

      {/* Load error */}
      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load revenue data</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {/* Monthly payments by stream */}
      <div className="mb-6">
        <div className="flex justify-between items-center flex-wrap gap-3 mb-3">
          <div>
            <div className="text-base font-bold text-ink">Monthly payments</div>
            <div className="text-[12.5px] text-g500">
              {monthLabel} · total {loading || !monthRow ? '…' : money(monthRow.total)}
            </div>
          </div>
          <select
            className="form-input"
            style={{ width: 170, padding: '7px 12px', fontWeight: 600 }}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            aria-label="Month"
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={m} value={i}>
                {m} {year}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-4 flex-wrap">
          {STREAMS.map((s) => {
            const amount = monthRow ? monthRow[s.type] : 0;
            const count = monthRow?.counts?.[s.type] ?? 0;
            const share = monthRow && monthRow.total > 0 ? Math.round((amount / monthRow.total) * 100) : 0;
            return (
              <div key={s.type} className="card flex-1 min-w-[220px] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: s.bg }}
                    >
                      <Icon name={s.icon} size={20} color={s.color} />
                    </div>
                    <span className="text-[13px] font-semibold text-g600 truncate">{s.label}</span>
                  </div>
                  <span
                    className="text-[11.5px] font-bold rounded-full px-2 py-0.5 flex-shrink-0"
                    style={{ background: s.bg, color: s.color }}
                    title={`Share of ${monthLabel} revenue`}
                  >
                    {share}%
                  </span>
                </div>
                <div className="text-[26px] font-extrabold text-ink leading-tight">
                  {loading ? '…' : money(amount)}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[12.5px] text-g500">
                    {count} payment{count === 1 ? '' : 's'} in {MONTH_NAMES[month]}
                  </span>
                  {count > 0 && (
                    <button
                      type="button"
                      className="text-[12.5px] font-semibold hover:underline"
                      style={{ color: s.color }}
                      onClick={() => viewPayments(s.type)}
                    >
                      View payments →
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div
        className="card p-6 mb-5 animate-fadeIn"
        style={{ animationDelay: '300ms' }}
      >
        <div className="flex justify-between items-center mb-6">
          <span className="text-base font-bold">Revenue Breakdown · {year}</span>
          <span className="text-[12px] text-g400">January – December {year}</span>
        </div>

        {breakdown.length === 0 || !yearHasRevenue ? (
          <div className="py-16 text-center text-g500">
            {loading ? 'Loading chart…' : `No revenue recorded in ${year}.`}
          </div>
        ) : (
          <div className="flex">
            <div
              className="flex flex-col justify-between"
              style={{ height: 180, paddingRight: 12, alignItems: 'flex-end' }}
            >
              <span className="text-[11px] text-g400 font-medium">
                ${Math.round(max).toLocaleString()}
              </span>
              <span className="text-[11px] text-g400 font-medium">
                ${Math.round(max * 0.75).toLocaleString()}
              </span>
              <span className="text-[11px] text-g400 font-medium">
                ${Math.round(max * 0.5).toLocaleString()}
              </span>
              <span className="text-[11px] text-g400 font-medium">
                ${Math.round(max * 0.25).toLocaleString()}
              </span>
              <span className="text-[11px] text-g400 font-medium">$0</span>
            </div>
            <div
              className="flex items-end gap-1.5"
              style={{ height: 200, padding: '0 8px', flex: 1 }}
            >
              {breakdown.map((b, monthIdx) => {
                const ch = Math.round((b.Commission / max) * 170);
                const ah = Math.round((b['App Fee'] / max) * 170);
                const sh = Math.round((b.Subscription / max) * 170);
                return (
                  <div
                    key={b.key}
                    className="flex-1 flex flex-col items-center gap-1"
                  >
                    <div
                      className="flex items-end gap-[3px] w-full"
                      style={{ height: 180 }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: ch,
                          background: 'var(--brand)',
                          borderRadius: '4px 4px 0 0',
                          transition: 'height .4s ease',
                        }}
                        title={`Legal Services Request: ${money(b.Commission)}`}
                      />
                      <div
                        style={{
                          flex: 1,
                          height: ah,
                          background: 'var(--green)',
                          borderRadius: '4px 4px 0 0',
                          transition: 'height .4s ease',
                        }}
                        title={`Rental Application: ${money(b['App Fee'])}`}
                      />
                      <div
                        style={{
                          flex: 1,
                          height: sh,
                          background: 'var(--blue)',
                          borderRadius: '4px 4px 0 0',
                          transition: 'height .4s ease',
                        }}
                        title={`Subscriptions: ${money(b.Subscription)}`}
                      />
                    </div>
                    <div
                      className="text-[11px] text-center"
                      style={{ color: isFutureMonth(monthIdx) ? 'var(--g300)' : 'var(--g500)' }}
                    >
                      {b.month}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-5 mt-4 justify-center flex-wrap">
          {[
            { l: 'Legal Services Request', c: 'var(--brand)' },
            { l: 'Rental Application', c: 'var(--green)' },
            { l: 'Subscriptions', c: 'var(--blue)' },
          ].map((leg) => (
            <div
              key={leg.l}
              className="flex items-center gap-1.5 text-[13px] text-g600 font-medium"
            >
              <span
                className="w-2.5 h-2.5 rounded"
                style={{ background: leg.c }}
              />
              {leg.l}
            </div>
          ))}
        </div>
      </div>

      {/* Transactions */}
      <div className="mb-5" ref={txnSectionRef} style={{ scrollMarginTop: 16 }}>
        {/* Search + date filter — applies to the transactions below. Dates
            apply as soon as they're picked; the text search runs on Search
            (or Enter). */}
        <form
          className="card p-4 mb-5 flex items-end flex-wrap gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(search.trim());
          }}
        >
          <div className="w-[260px]">
            <label className="form-label" htmlFor="txn-search">
              Search
            </label>
            <input
              id="txn-search"
              type="search"
              className="form-input"
              placeholder="Name or transaction ID"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-[170px]">
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="w-[170px]">
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">
            <Icon name="search" size={14} color="#fff" /> Search
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setSearch('');
              setAppliedSearch('');
            }}
            disabled={!hasFilters}
            style={hasFilters ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
          >
            <Icon name="x" size={14} /> Clear
          </button>
          {hasRange && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 ml-auto pl-2">
              <div>
                <div className="text-[12px] text-g500">{rangeLabel}</div>
                <div className="text-[22px] font-extrabold text-ink leading-tight">
                  {loading || !period ? '…' : money(period.total)}
                </div>
              </div>
              {period &&
                ([
                  ['Commission', 'Legal Services Request', 'var(--brand)'],
                  ['App Fee', 'Rental Application', 'var(--green)'],
                  ['Subscription', 'Subscriptions', 'var(--blue)'],
                ] as const).map(([key, label, color]) => (
                  <div key={key}>
                    <div className="text-[12px] text-g500 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded" style={{ background: color }} />
                      {label}
                    </div>
                    <div className="text-[15px] font-bold text-ink">{money(period.byType[key])}</div>
                  </div>
                ))}
            </div>
          )}
        </form>

        <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="capitalize"
                  style={{
                    padding: '8px 16px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                    background: active ? 'var(--brand-light)' : '#fff',
                    color: active ? 'var(--brand)' : 'var(--g600)',
                  }}
                >
                  {f === 'all' ? 'All' : typeLabel(f)}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="bg-white rounded-card overflow-hidden border border-g100"
          style={{
            boxShadow:
              '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {[
                  'Transaction ID',
                  'Type',
                  'Amount',
                  partyLabel(filter),
                  'Reference',
                  'Date',
                  'Status',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txnLoading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="py-12 text-center text-g500">
                      Loading transactions...
                    </div>
                  </td>
                </tr>
              ) : txns.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="py-12 text-center text-g500">
                      {appliedSearch
                        ? `No transactions match "${appliedSearch}"${hasRange ? ' in this period' : ''}.`
                        : hasRange
                        ? 'No transactions in this period.'
                        : 'No transactions yet.'}
                    </div>
                  </td>
                </tr>
              ) : (
                txns.map((t) => (
                  <tr key={t.id} className="hover:bg-g50 transition-colors">
                    <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                      {t.transactionRef || `TXN-${t.id}`}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span
                        className="type-badge"
                        style={txnTypeStyle(t.type)}
                      >
                        {typeLabel(t.type)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                      {moneyExact(t.amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      {t.counterparty?.name ||
                        t.counterpartyName || (
                          <span className="text-g400 italic">—</span>
                        )}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {t.reference || '—'}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {formatDate(t.occurredAt)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span
                        className={
                          'badge ' +
                          (t.status === 'paid'
                            ? 'badge-active'
                            : t.status === 'pending'
                            ? 'badge-pending'
                            : 'badge-rejected')
                        }
                      >
                        {t.status[0].toUpperCase() + t.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={txnTotal} onChange={setPage} disabled={txnLoading} />
      </div>

    </div>
  );
};

export default Revenue;
